import { useEffect, useId, useState } from 'react';
import { Button } from '@/components/ui-kit/button';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import { cn } from '@/lib/utils';
import type { ReportRangeDays } from '../types/reports.types';
import { useReportRange } from '../hooks/use-report-range';
import { fmtShortDate, isoDate } from '../utils/report-format';

type ReportRangeControlProps = {
  className?: string;
  /** Must match the page’s useReportRange default when the URL has no days/from/to. */
  defaultDays?: ReportRangeDays;
};

export function ReportRangeControl({ className, defaultDays = 30 }: ReportRangeControlProps) {
  const { range, setPreset, setRange, presets, presetLabels } = useReportRange(defaultDays);
  const [open, setOpen] = useState(false);
  const [from, setFrom] = useState(range.from);
  const [to, setTo] = useState(range.to);
  const [error, setError] = useState('');
  const fromId = useId();
  const toId = useId();
  const today = isoDate(new Date());

  useEffect(() => {
    if (!open) return;
    setFrom(range.from);
    setTo(range.to);
    setError('');
  }, [open, range.from, range.to]);

  const applyCustom = () => {
    if (!from || !to) {
      setError('Pick both a start and an end date.');
      return;
    }
    if (from > to) {
      setError('The start date is after the end date.');
      return;
    }
    const fromMs = new Date(`${from}T00:00:00`).getTime();
    const toMs = new Date(`${to}T00:00:00`).getTime();
    const days = Math.max(1, Math.round((toMs - fromMs) / 86400000) + 1);
    setRange({
      days,
      from,
      to,
      label: `${fmtShortDate(from)} – ${fmtShortDate(to)}`,
      isCustom: true,
    });
    setOpen(false);
  };

  const customLabel = range.isCustom
    ? `${fmtShortDate(range.from)} – ${fmtShortDate(range.to)}`
    : 'Custom…';

  return (
    <div
      className={cn(
        'relative inline-flex items-center rounded-md border border-neutral-300 bg-white p-0.5',
        className
      )}
      role="group"
      aria-label="Date range"
    >
      {presets.map((days) => {
        const active = !range.isCustom && range.days === days;
        return (
          <button
            key={days}
            type="button"
            aria-pressed={active}
            onClick={() => {
              setOpen(false);
              setPreset(days as ReportRangeDays);
            }}
            className={cn(
              'whitespace-nowrap rounded-sm px-3 py-1.5 text-xs font-medium text-neutral-800',
              active
                ? 'bg-primary-600 text-white hover:bg-primary-600'
                : 'bg-transparent hover:bg-neutral-100'
            )}
          >
            {presetLabels[days]}
          </button>
        );
      })}

      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-pressed={range.isCustom}
            aria-haspopup="dialog"
            className={cn(
              'whitespace-nowrap rounded-sm px-3 py-1.5 text-xs font-medium',
              range.isCustom
                ? 'bg-primary-600 text-white hover:bg-primary-600'
                : 'bg-transparent text-neutral-800 hover:bg-neutral-100'
            )}
          >
            {customLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          sideOffset={6}
          className="w-[250px] rounded-md border border-neutral-200 bg-white p-3 shadow-lg"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div
            role="dialog"
            aria-label="Custom date range"
            className="flex flex-col gap-2.5"
          >
            <div className="flex flex-col gap-1">
              <Label htmlFor={fromId} className="text-xs text-neutral-700">
                From
              </Label>
              <Input
                id={fromId}
                type="date"
                value={from}
                max={today}
                onChange={(e) => {
                  setFrom(e.target.value);
                  setError('');
                }}
                className="h-9 text-sm"
                autoFocus
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={toId} className="text-xs text-neutral-700">
                To
              </Label>
              <Input
                id={toId}
                type="date"
                value={to}
                max={today}
                onChange={(e) => {
                  setTo(e.target.value);
                  setError('');
                }}
                className="h-9 text-sm"
              />
            </div>
            {error ? (
              <p className="text-xs text-[#B3261E]" role="alert">
                {error}
              </p>
            ) : null}
            <div className="flex items-center justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={() => setOpen(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-8 px-3 text-xs"
                onClick={applyCustom}
              >
                Apply
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
